import {
  FormValues,
  type FormActions,
  type FormConfig,
  type FormResults,
} from '@wix/form-viewer';
import { bookingsUoUBookingFormClickSrc16Evid83 } from '@wix/bi-logger-bookings-data/v2';
import { FormViewerContext, FormViewerHandle } from '@wix/form-viewer/widget';
import { useSettings } from '@wix/tpa-settings/react';
import { withSlotsPlaceholders } from '@wix/widget-plugins-ooi';
import {
  useBi,
  useEnvironment,
  useErrorBoundary,
  useExperiments,
  useTranslation,
  WidgetProps,
} from '@wix/yoshi-flow-editor';
import loadable from '@wix/yoshi-flow-editor/loadable';
import React, { FC, Suspense, useCallback, useEffect, useState } from 'react';
import {
  CartConflictErrorType,
  DynamicPriceErrorType,
  EmptyStateErrorType,
  FormError,
  GenericErrorType,
} from '../../../types/errors';
import {
  BookingFormClickActions,
  BookingFormClickComponents,
} from '../../../types/biLoggerTypes';
import { ExperimentsConsts } from '../../../consts/experiments';
import { FormStatus } from '../../../types/form-state';
import { DialogType, PaymentMethod } from '../../../types/types';
import { mapToArray } from '../../../utils';
import { getErrorByType, hasErrorOfType } from '../../../utils/errors/errors';
import {
  FormState,
  IntakeForm,
  IntakeFormStatus,
  ServiceData,
  Participant,
  SlotService,
} from '../../../utils/state/types';
import { Status } from '@wix/ambassador-intake-forms-v1-intake-form-submission/types';
import { ApprovalMode } from '@wix/ambassador-intake-forms-v1-intake-form/types';
import { Modal } from '../Modal';
import { isServiceExist } from '../../../utils/validations/Validations';
import settingsParams from '../settingsParams';
import { FormControllerActions } from '../Actions/actions';
import { EditorContextProvider } from '../Hooks/useEditorContext';
import { FormActionsProvider } from '../Hooks/useFormActions';
import { useBfcacheStatusReset } from '../Hooks/useBfcacheStatusReset';
import { FormComponentContextProvider } from '../Hooks/useFormComponentContext';
import useValidateWixForm from '../Hooks/useValidateWixForm';
import { AddonsDetails } from './AddonsDetails';
import { RelatedProducts } from './RelatedProducts';
import { AddonsNotification } from './AddonsNotification';
import AlreadyBookedModal from './AlreadyBookedModal';
import BackButton from './BackButton/BackButton';
import BookingDetails from './BookingDetails/BookingDetails';
import ButtonsContainer from './ButtonsContainer';
import { CartConflictMessage } from './CartConflictMessage/CartConflictMessage';
import CartErrorModal from './CartErrorModal';
import CartModal from './CartModal/CartModal';
import { CouponCheckbox } from './CouponCheckbox';
import { WidgetDataHooks } from './dataHooks';
import EmptyStatePage from './EmptyStatePage/EmptyStatePage';
import Header from './Header';
import { handleIntakeFormEmailValidation } from './IntakeFormSection/handleIntakeFormEmailValidation';
import IntakeFormSection from './IntakeFormSection/IntakeFormSection';
import NumberOfParticipantsDropdown from './NumberOfParticipantsDropdown/NumberOfParticipantsDropdown';
import { PaymentSelectionContainer } from './PaymentSelectionContainer/PaymentSelectionContainer';
import { PaymentSummary } from './PaymentSummary/PaymentSummary';
import { ServiceInCartNotification } from './ServiceInCartNotification';
import SmsNotification from './SmsNotification/SmsNotification';
import { UnavailableSlotMessage } from './UnavailableSlotMessage';
import { classes, st } from './Widget.st.css';
import WixForm from './WixForm';
import ChevronLeft from 'wix-ui-icons-common/on-stage/ChevronLeftSmall';
import ChevronRight from 'wix-ui-icons-common/on-stage/ChevronRightSmall';
import BookForSomeoneElseCheckbox from './BookForSomeoneElseCheckbox';
import BookForSomeoneElseBanner from './BookForSomeoneElseBanner';
import {
  getIsMultiParticipantFlowActive,
  getMaxParticipants,
  hasPricingPlanOnlyService,
} from '../../../utils/multiParticipantFlow';
import ParticipantVariantPicker from './ParticipantVariantPicker';
import ParticipantSection from './ParticipantSection/ParticipantSection';
import {
  aggregateParticipantVariants,
  expandParticipantVariants,
} from '../../../utils/mappers/aggregateParticipantVariants';
import {
  createParticipant,
  EMPTY_PARTICIPANT_VARIANT,
  getParticipantName,
  getParticipantVariants,
} from '../../../utils/multiParticipantFlow/participants';
import ParticipantActionButton, {
  ParticipantActionButtonDataHooks,
} from './ParticipantActionButton';
import Plus from 'wix-ui-icons-common/on-stage/Plus';

const Dialog = loadable(() => import('./Dialog/Dialog'));
const Toast = loadable(() => import('./Toast/Toast'));
const PolicyDescription = loadable(
  () => import('./PolicyDescription/PolicyDescription'),
);

const showGlobalLoaderList = [
  FormStatus.PROCESSING_SUBMIT_ACTION,
  FormStatus.REINITIALIZING,
];

// Rendered before the controller has seeded `participants` (first paint of the flow), so
// the booker's form is on screen immediately; the seed uses the same id.
const BOOKER_PARTICIPANT_ID = 'booker';
const FALLBACK_PARTICIPANTS: Participant[] = [
  { id: BOOKER_PARTICIPANT_ID, selectedAddons: {} },
];

export type ControllerProps = {
  actions: FormControllerActions;
} & FormState &
  WidgetProps & {
    wfActions: FormActions;
    wfConfig: FormConfig;
    wfResults: FormResults;
  };

const Widget: FC<WidgetProps<ControllerProps>> = (widgetProps) => {
  const {
    intakeForms,
    serviceData,
    actions,
    isPricingPlanInstalled,
    formSelectedSlot,
    businessInfo,
    isCart,
    couponInfo,
    memberDetails,
    isLoggedIn,
    errors,
    editorContext,
    status,
    dialog,
    cartModal,
    isServiceInCart,
    isUpsellPluginInstalled,
    existingBookingDetails,
    wixForms,
    selectedNumberOfParticipants,
    isSMSAutomationsEnabled,
    isSlotUnavailable,
    selectedProductAddons,
    relatedProducts,
    storesAppInstance,
    isBookingForSomeoneElse,
    isPricingPlansMigratedToEcom,
  } = widgetProps;
  const { isMobile, isSSR, isRTL } = useEnvironment();
  const { error } = useErrorBoundary();
  const settings = useSettings();
  const bi = useBi();
  const { experiments } = useExperiments();
  const { t } = useTranslation();
  const dateRegionalSettingsLocale = businessInfo?.dateRegionalSettingsLocale!;
  const { validateWixForm } = useValidateWixForm();
  const wixFormRef = React.useRef<FormViewerHandle>(null);
  // Multi-participant flow: every participant section stays mounted (collapsed ones are
  // hidden), so each keeps its own form-viewer handle and DOM node for validation/scroll.
  // Keyed by participant id. Stable mutable maps (not useRef: specs stub React.useRef
  // for the form handle).
  const [participantFormRefs] = useState<{
    current: Record<string, FormViewerHandle | null>;
  }>(() => ({ current: {} }));
  const [participantSectionRefs] = useState<{
    current: Record<string, HTMLDivElement | null>;
  }>(() => ({ current: {} }));
  const [wixFormValues, setWixFormValues] = useState<FormValues>({});
  // Per-participant form values, keyed by participant id (kept out of controller state:
  // it is serialized to the widget on every update).
  const [participantForms, setParticipantForms] = useState<
    Record<string, FormValues>
  >({});
  const [expandedParticipantIndex, setExpandedParticipantIndex] = useState(0);
  // The participant whose form takes focus: the one just added or just expanded by
  // the visitor. null until the visitor interacts, so nothing is focused on load.
  const [focusedParticipantIndex, setFocusedParticipantIndex] = useState<
    number | null
  >(null);
  // Forms currently BUSY (e.g. an upload in progress). The global form status is
  // processing while any of them is, so one form finishing can't re-enable Book while
  // another is still working.
  const [busyParticipantForms] = useState<{ current: Set<number> }>(() => ({
    current: new Set(),
  }));
  const handleParticipantFormBusyChange = useCallback(
    (index: number) => (isBusy: boolean) => {
      const wasBusy = busyParticipantForms.current.size > 0;
      if (isBusy) {
        busyParticipantForms.current.add(index);
      } else {
        busyParticipantForms.current.delete(index);
      }
      const isAnyBusy = busyParticipantForms.current.size > 0;
      if (isAnyBusy !== wasBusy) {
        actions.setFormStatus(
          isAnyBusy ? FormStatus.GENERAL_PROCESS : FormStatus.IDLE,
        );
      }
    },
    [actions, busyParticipantForms],
  );

  const isBookForSomeoneElseEnabled = experiments.enabled(
    ExperimentsConsts.BookForSomeoneElse,
  );
  const isBookForSomeoneElseAvailable =
    isBookForSomeoneElseEnabled &&
    !!serviceData?.allowBookForSomeoneElse &&
    !hasPricingPlanOnlyService(serviceData);
  const isBookForSomeoneElseActive =
    isBookForSomeoneElseAvailable && isBookingForSomeoneElse;

  const isMultiParticipantFlowActive = getIsMultiParticipantFlowActive(
    serviceData,
    experiments.enabled(ExperimentsConsts.CollectParticipantForm),
  );
  const maxParticipants = getMaxParticipants(serviceData);

  const [hasLoggedInViaModal, setHasLoggedInViaModal] = useState(false);
  const isRelatedProductsOnServicesEnabled = experiments.enabled(
    ExperimentsConsts.RelatedProductsOnServices,
  );
  const handleWixFormChange = useCallback(
    (values: FormValues) => {
      setWixFormValues(values);
      handleIntakeFormEmailValidation(values, intakeForms, actions, bi);
    },
    [intakeForms, actions, bi],
  );

  const handleParticipantFormChange = useCallback(
    (participantId: string) => (values: FormValues) => {
      setParticipantForms((prev) => ({ ...prev, [participantId]: values }));
    },
    [],
  );

  const serviceSlotKey = Object.keys(serviceData?.slotServices ?? {})[0];
  const participantSlotService = serviceData?.slotServices?.[serviceSlotKey];
  // The slot's participants are the source of truth (they survive re-initialization).
  const participants: Participant[] = participantSlotService?.participants
    ?.length
    ? participantSlotService.participants
    : FALLBACK_PARTICIPANTS;
  // Open spots in the session, minus the ones this booking is already taking.
  const availableSpots = participantSlotService?.service.availableSpots;
  const participantFormValues = participants.map(
    ({ id }) => participantForms[id] ?? {},
  );

  // Indexes of participants who still have to pick a price variant (when the service
  // has variants at all).
  const getParticipantsMissingVariant = useCallback((): number[] => {
    if (!participantSlotService?.dynamicPriceInfo?.customOptions?.length) {
      return [];
    }
    return participants
      .map((participant, index) => ({ participant, index }))
      .filter(
        ({ participant }) => !participant.selectedVariant?.choices?.length,
      )
      .map(({ index }) => index);
  }, [participantSlotService, participants]);

  // Expand a participant's section and bring it into view (used after validation).
  const revealParticipant = useCallback(
    (index: number) => {
      setExpandedParticipantIndex(index);
      setTimeout(() => {
        const id = participants[index]?.id;
        participantSectionRefs.current[id]?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'start',
        });
      }, 0);
    },
    [participants],
  );

  // Seed the participants once the flow activates: one per calendar-preselected variant
  // (capped), otherwise just the booker.
  useEffect(() => {
    if (
      !isMultiParticipantFlowActive ||
      !participantSlotService ||
      participantSlotService.participants
    ) {
      return;
    }
    const seededVariants = expandParticipantVariants(
      participantSlotService.dynamicPriceInfo?.selectedVariants,
    ).slice(0, maxParticipants);
    const count = seededVariants.length || 1;
    const seeded: Participant[] = Array.from({ length: count }, (_, index) =>
      createParticipant({
        ...(index === 0 ? { id: BOOKER_PARTICIPANT_ID } : {}),
        selectedVariant: seededVariants[index] ?? EMPTY_PARTICIPANT_VARIANT,
      }),
    );
    actions.updateServiceSlot({ participants: seeded }, serviceSlotKey);
    if (count > 1) {
      actions.setNumberOfParticipants(count, serviceSlotKey);
    }
    // The denominator for the add/remove clicks: how many forms offered the CTA at
    // all, and how many participants they already started with.
    actions.logBi([
      bookingsUoUBookingFormClickSrc16Evid83({
        component: BookingFormClickComponents.AddParticipant,
        action: BookingFormClickActions.Load,
        value: String(count),
      }),
    ]);
  }, [
    isMultiParticipantFlowActive,
    participantSlotService,
    serviceSlotKey,
    actions,
    maxParticipants,
  ]);

  const updateParticipants = useCallback(
    (nextParticipants: Participant[]) => {
      if (!participantSlotService) {
        return;
      }
      const { dynamicPriceInfo } = participantSlotService;
      actions.updateServiceSlot(
        {
          participants: nextParticipants,
          ...(dynamicPriceInfo?.customOptions?.length
            ? {
                dynamicPriceInfo: {
                  ...dynamicPriceInfo,
                  selectedVariants: aggregateParticipantVariants(
                    getParticipantVariants(nextParticipants),
                  ),
                },
              }
            : {}),
        },
        serviceSlotKey,
      );
    },
    [actions, serviceSlotKey, participantSlotService],
  );

  const addParticipant = useCallback(() => {
    actions.clearErrorByTypes([DynamicPriceErrorType]);
    const nextCount = participants.length + 1;
    // The new participant opens and takes focus; the one being edited collapses.
    setExpandedParticipantIndex(participants.length);
    setFocusedParticipantIndex(participants.length);
    // No payment yet: the payment section applies the default (the previous
    // participant's choice, or the regular default if that was a plan).
    updateParticipants([
      ...participants,
      createParticipant({ selectedVariant: EMPTY_PARTICIPANT_VARIANT }),
    ]);
    actions.setNumberOfParticipants(nextCount, serviceSlotKey);
    actions.logBi([
      bookingsUoUBookingFormClickSrc16Evid83({
        component: BookingFormClickComponents.AddParticipant,
        action: BookingFormClickActions.Click,
        value: String(nextCount),
      }),
    ]);
  }, [participants, actions, serviceSlotKey, updateParticipants]);

  const removeParticipant = useCallback(
    (removedIndex: number) => {
      const removed = participants[removedIndex];
      if (participants.length <= 1 || removedIndex === 0 || !removed) {
        return;
      }
      const nextCount = participants.length - 1;
      // Indexes shift after a removal; drop the busy bookkeeping and let the remaining
      // forms report again (a form that is still BUSY re-reports on its next status change).
      if (busyParticipantForms.current.size) {
        busyParticipantForms.current.clear();
        actions.setFormStatus(FormStatus.IDLE);
      }
      updateParticipants(participants.filter(({ id }) => id !== removed.id));
      setParticipantForms(({ [removed.id]: _dropped, ...rest }) => rest);
      participantFormRefs.current[removed.id] = null;
      participantSectionRefs.current[removed.id] = null;
      // Keep the same participant open; if it was the removed one, open the previous.
      // Indexes shift, so focus has to follow rather than keep the old number.
      const shiftIndex = (index: number) =>
        index >= removedIndex ? Math.max(0, index - 1) : index;
      setExpandedParticipantIndex(shiftIndex);
      setFocusedParticipantIndex((focused) =>
        focused === null ? null : shiftIndex(focused),
      );
      actions.clearErrorByTypes([DynamicPriceErrorType]);
      actions.setNumberOfParticipants(nextCount, serviceSlotKey);
      actions.logBi([
        bookingsUoUBookingFormClickSrc16Evid83({
          component: BookingFormClickComponents.RemoveParticipant,
          action: BookingFormClickActions.Click,
          value: String(nextCount),
        }),
      ]);
    },
    [
      participants,
      actions,
      serviceSlotKey,
      updateParticipants,
      participantFormRefs,
      participantSectionRefs,
      busyParticipantForms,
    ],
  );

  useEffect(() => {
    if (
      status === FormStatus.INITIALIZING ||
      status === FormStatus.REINITIALIZING
    ) {
      actions.initializeWidget();
    }
  }, [status, actions]);

  useBfcacheStatusReset({
    status,
    setFormStatus: actions.setFormStatus,
    experiments,
  });

  useEffect(() => {
    if (!isBookForSomeoneElseActive) {
      setWixFormValues({ ...wixFormValues, ...wixForms.initialValues });
    }
  }, [wixForms.initialValues, isBookForSomeoneElseActive]);

  useEffect(() => {
    if (isBookForSomeoneElseActive) {
      setWixFormValues({});
    } else {
      setWixFormValues(wixForms.initialValues);
    }
  }, [isBookForSomeoneElseActive]);

  useEffect(() => {
    setParticipantForms((prev) => ({
      ...prev,
      [BOOKER_PARTICIPANT_ID]: isBookForSomeoneElseActive
        ? {}
        : { ...prev[BOOKER_PARTICIPANT_ID], ...wixForms.initialValues },
    }));
  }, [wixForms.initialValues, isBookForSomeoneElseActive]);

  const effectiveWixForms = isBookForSomeoneElseActive
    ? { ...wixForms, overrides: {} }
    : wixForms;
  // Participants after the first are always someone else: no member prefill/read-only fields.
  const getParticipantWixForms = (index: number) =>
    index > 0 ? { ...wixForms, overrides: {} } : effectiveWixForms;

  const renderedEmptyState = renderEmptyStatePage({
    error,
    status,
    errors,
    serviceData,
    experiments,
  });

  if (renderedEmptyState) {
    return renderedEmptyState;
  }

  const toastError = getErrorByType({
    // The pending submission (NO_VALID_INTAKE_FORM_SUBMISSION) is surfaced via the pending dialog,
    // not as a raw toast.
    errors: errors.filter(
      (e) => e.errorType !== GenericErrorType.NO_VALID_INTAKE_FORM_SUBMISSION,
    ),
    errorType: GenericErrorType,
  });

  const cartConflictError = getErrorByType({
    errors,
    errorType: CartConflictErrorType,
  });

  const intakeFormsArray = mapToArray<IntakeForm>(intakeForms ?? {});

  // Anonymous + a pending-approval intake form → block the booking form entirely and prompt login
  // first (Strategy A, §8): a pending submission must be tracked against a contact. A form is
  // pending-approval when it has trigger fields (MANUAL_APPROVE_TRIGGER_FIELDS) or is set to
  // MANUAL_APPROVE_ALL (every submission pending, no triggers configured).
  const requiresLoginForIntakeForm =
    !isLoggedIn &&
    intakeFormsArray.some(
      (form) =>
        form.approvalMode === ApprovalMode.MANUAL_APPROVE_ALL ||
        (form.pendingTriggerFields?.length ?? 0) > 0,
    );
  // Hard block: the booking form is never rendered for an anonymous visitor on a triggered form —
  // only the login modal (no dismiss/bypass). They must log in before anything loads. After a
  // successful login the modal auto-closes (the form then renders).
  if (requiresLoginForIntakeForm && !hasLoggedInViaModal) {
    return (
      <Modal
        dataHook="intake-form-login-modal"
        withoutCloseButton
        modalTitle={t('app.intake-form-section.login-modal.title')}
        modalSubtitle={t('app.intake-form-section.login-modal.subtitle')}
        primaryButton={{
          label: t('app.intake-form-section.login-modal.cta'),
          onClick: async () => {
            const loggedIn = await actions.promptLogin();
            if (loggedIn) {
              setHasLoggedInViaModal(true);
            }
          },
        }}
      />
    );
  }

  const onContactBusiness = () => {
    void actions.contactBusiness();
  };

  // A pending submission no longer blocks the whole form on load — the intake section renders the
  // pending state inline. Only when the customer clicks Book is it rejected with
  // NO_VALID_INTAKE_FORM_SUBMISSION; then the pending dialog is OVERLAID on the form (the form stays
  // behind it) and is dismissible — no redirect (§7.5).
  const hasNoValidIntakeSubmissionError = errors.some(
    (e) => e.errorType === GenericErrorType.NO_VALID_INTAKE_FORM_SUBMISSION,
  );
  const pendingFormName = intakeFormsArray.find(
    (form) =>
      form.status === IntakeFormStatus.PENDING_APPROVAL ||
      form.previousSubmissionStatus === Status.PENDING_APPROVAL,
  )?.formName;
  // Close the dialog without leaving the booking form: clear the book-time error.
  const closePendingModal = () =>
    actions.removeErrors([GenericErrorType.NO_VALID_INTAKE_FORM_SUBMISSION]);

  const showGlobalLoader = () => showGlobalLoaderList.includes(status);

  return (
    <FormViewerContext
      host={widgetProps.host}
      wfActions={widgetProps.wfActions}
      wfConfig={widgetProps.wfConfig}
      wfResults={widgetProps.wfResults}
    >
      <EditorContextProvider value={editorContext} key="form-main-widget">
        <FormActionsProvider
          value={{
            ...actions,
            validateWixForm: async () => {
              if (!isMultiParticipantFlowActive) {
                return validateWixForm({
                  actions,
                  serviceData,
                  wixFormRef,
                  intakeForms,
                  wixFormValues,
                });
              }
              // Validate every participant so each form shows its own inline errors,
              // then open and scroll to the first one that failed. The booker's form
              // also carries the booking-level (dynamic price / intake form) checks.
              const formResults = await Promise.all(
                participants.map(({ id }, index) => {
                  const values = participantForms[id] ?? {};
                  const ref = {
                    current: participantFormRefs.current[id] ?? undefined,
                  };
                  return index === 0
                    ? validateWixForm({
                        actions,
                        serviceData,
                        wixFormRef: ref,
                        intakeForms,
                        wixFormValues: values,
                      })
                    : ref.current?.validate?.() ?? Promise.resolve(false);
                }),
              );
              // One error naming every participant that is missing a variant, so only
              // their pickers turn red. It has to be a single error: addError replaces
              // any existing one of the same type, so a loop would keep only the last.
              const missingVariant = getParticipantsMissingVariant();
              if (missingVariant.length) {
                actions.addError(
                  DynamicPriceErrorType.MISSING_REQUIRED_FIELD_DATA,
                  {
                    message: t(
                      'app.form-inputs.validation-errors.required-field',
                    ),
                    participantIndexes: missingVariant,
                  },
                );
              }
              const firstInvalid = formResults.findIndex(
                (isValid, index) => !isValid || missingVariant.includes(index),
              );
              if (firstInvalid === -1) {
                return true;
              }
              revealParticipant(firstInvalid);
              return false;
            },
          }}
        >
          <FormComponentContextProvider
            value={{
              wixFormValues,
              participantFormValues: isMultiParticipantFlowActive
                ? participantFormValues
                : [wixFormValues],
              isCartConflictError: !!cartConflictError,
              isUpsellPluginInstalled,
              isPricingPlansMigratedToEcom,
              dateRegionalSettingsLocale,
            }}
          >
            <Suspense
              fallback={
                <RenderGlobalLoader
                  dataHook={WidgetDataHooks.SUSPENSE_GLOBAL_SPINNER}
                  showGlobalLoader
                />
              }
            >
              <div
                className={st(classes.root, {
                  isMobile,
                  isProcessing: showGlobalLoader(),
                })}
                data-hook={WidgetDataHooks.MAIN_CONTAINER}
              >
                <div className={classes.wrapper}>
                  <RenderGlobalLoader showGlobalLoader={showGlobalLoader()} />
                  {toastError ? <Toast toastError={toastError} /> : null}
                  <BackButton />
                  {isSlotUnavailable && <UnavailableSlotMessage />}
                  <Header />
                  {cartConflictError ? (
                    <CartConflictMessage />
                  ) : (
                    <ServiceInCartNotification
                      isServiceInCart={isServiceInCart}
                    />
                  )}
                  <div className={classes.body}>
                    <div className={classes.formWrapper}>
                      {isMultiParticipantFlowActive ? (
                        <>
                          {participants.map(({ id }, index) => (
                            <ParticipantSection
                              key={id}
                              ref={(node) => {
                                participantSectionRefs.current[id] = node;
                              }}
                              participantIndex={index}
                              totalParticipants={participants.length}
                              isExpanded={expandedParticipantIndex === index}
                              onToggle={() => {
                                const isOpening =
                                  expandedParticipantIndex !== index;
                                setExpandedParticipantIndex(
                                  isOpening ? index : -1,
                                );
                                // Cleared on collapse so re-opening the same section
                                // changes `autoFocus` again and moves focus once more.
                                setFocusedParticipantIndex(
                                  isOpening ? index : null,
                                );
                              }}
                              onRemove={
                                index > 0
                                  ? () => removeParticipant(index)
                                  : undefined
                              }
                              title={getParticipantName(participantForms[id])}
                            >
                              {index === 0 && isBookForSomeoneElseAvailable && (
                                <BookForSomeoneElseCheckbox
                                  isBookingForSomeoneElse={
                                    !!isBookingForSomeoneElse
                                  }
                                  isPricingPlanSelected={isPricingPlanSelected(
                                    serviceData,
                                  )}
                                />
                              )}
                              {index === 0 && isBookForSomeoneElseActive && (
                                <BookForSomeoneElseBanner />
                              )}
                              {/* Own FormViewerContext with a per-participant compId:
                                  form-fields derives input/label ids from it, so
                                  several instances of the same form stay unique. */}
                              <FormViewerContext
                                host={widgetProps.host}
                                wfActions={widgetProps.wfActions}
                                wfResults={widgetProps.wfResults}
                                wfConfig={{
                                  ...widgetProps.wfConfig,
                                  // Keyed by the participant id (not position) so a
                                  // surviving form keeps its DOM ids when one is removed.
                                  compId: `${
                                    widgetProps.wfConfig?.compId ?? 'comp'
                                  }-${id}`,
                                  // Participants after the first are someone else:
                                  // never autofill the logged-in booker's details.
                                  ...(index > 0 ? { userData: undefined } : {}),
                                }}
                              >
                                <WixForm
                                  formId={serviceData.formId}
                                  wixFormRef={{
                                    get current() {
                                      return participantFormRefs.current[id];
                                    },
                                    set current(handle) {
                                      participantFormRefs.current[id] = handle;
                                    },
                                  }}
                                  wixForms={getParticipantWixForms(index)}
                                  formValues={participantForms[id] ?? {}}
                                  handleFormChange={handleParticipantFormChange(
                                    id,
                                  )}
                                  onBusyChange={handleParticipantFormBusyChange(
                                    index,
                                  )}
                                  autoFocus={focusedParticipantIndex === index}
                                />
                              </FormViewerContext>
                              <ParticipantVariantPicker
                                serviceData={serviceData}
                                participantIndex={index}
                                status={status}
                                errors={errors}
                              />
                              {!isSSR && isServiceExist(formSelectedSlot) && (
                                <PaymentSelectionContainer
                                  serviceData={serviceData}
                                  memberDetails={memberDetails}
                                  numberOfParticipants={1}
                                  isPricingPlanInstalled={
                                    isPricingPlanInstalled
                                  }
                                  status={status}
                                  errors={errors}
                                  isCart={isCart}
                                  isBookingForSomeoneElse={
                                    index === 0 && isBookForSomeoneElseActive
                                  }
                                  participantIndex={index}
                                />
                              )}
                              <AddonsDetails
                                serviceData={serviceData}
                                participantIndex={index}
                              />
                            </ParticipantSection>
                          ))}
                          <ParticipantActionButton
                            dataHook={ParticipantActionButtonDataHooks.ADD}
                            icon={<Plus aria-hidden />}
                            label={t(
                              'bookings.form.collectParticipant.addParticipants.label',
                            )}
                            hint={
                              availableSpots !== undefined
                                ? t(
                                    'bookings.form.collectParticipant.spotsLeft.label',
                                    {
                                      // maxParticipants already caps the button, so
                                      // the hint counts down to the same ceiling.
                                      count: Math.max(
                                        0,
                                        maxParticipants - participants.length,
                                      ),
                                    },
                                  )
                                : undefined
                            }
                            onClick={addParticipant}
                            disabled={participants.length >= maxParticipants}
                            tooltipContent={
                              participants.length >= maxParticipants
                                ? t(
                                    'bookings.form.collectParticipant.addParticipant.maxReachedTooltip',
                                  )
                                : undefined
                            }
                          />
                        </>
                      ) : (
                        <>
                          {isBookForSomeoneElseAvailable && (
                            <BookForSomeoneElseCheckbox
                              isBookingForSomeoneElse={
                                !!isBookingForSomeoneElse
                              }
                              isPricingPlanSelected={isPricingPlanSelected(
                                serviceData,
                              )}
                            />
                          )}
                          {isBookForSomeoneElseActive && (
                            <BookForSomeoneElseBanner />
                          )}
                          <WixForm
                            formId={serviceData.formId}
                            wixFormRef={wixFormRef}
                            wixForms={effectiveWixForms}
                            formValues={wixFormValues}
                            handleFormChange={handleWixFormChange}
                          />
                          <NumberOfParticipantsDropdown
                            serviceData={serviceData}
                          />
                        </>
                      )}
                      {!isSSR &&
                        !isMultiParticipantFlowActive &&
                        isServiceExist(formSelectedSlot) && (
                          <PaymentSelectionContainer
                            serviceData={serviceData}
                            memberDetails={memberDetails}
                            numberOfParticipants={selectedNumberOfParticipants}
                            isPricingPlanInstalled={isPricingPlanInstalled}
                            status={status}
                            errors={errors}
                            isCart={isCart}
                            isBookingForSomeoneElse={isBookForSomeoneElseActive}
                          />
                        )}
                      <IntakeFormSection
                        intakeForms={intakeForms}
                        errors={errors}
                        isSingleService={serviceData.isSingleService}
                        onContactBusiness={onContactBusiness}
                      />
                      {!isMultiParticipantFlowActive && (
                        <AddonsDetails serviceData={serviceData} />
                      )}
                      {isRelatedProductsOnServicesEnabled &&
                        settings.get(
                          settingsParams.relatedProductsSectionVisible,
                        ) && (
                          <RelatedProducts
                            products={relatedProducts}
                            selection={selectedProductAddons}
                            onSelectionChange={actions.setSelectedProductAddons}
                            onProductEnriched={
                              actions.setEnrichedRelatedProduct
                            }
                            storesAppInstance={storesAppInstance}
                          />
                        )}
                    </div>
                    <div className={classes.sidebar}>
                      <div className={classes.floatingContainer}>
                        <BookingDetails
                          serviceData={serviceData}
                          formSelectedSlot={formSelectedSlot}
                          numberOfParticipants={selectedNumberOfParticipants}
                          isCart={isCart}
                          selectedProductAddons={selectedProductAddons}
                          relatedProducts={relatedProducts}
                        />
                        <CouponCheckbox
                          serviceData={serviceData}
                          couponInfo={couponInfo}
                          isServiceInCart={isServiceInCart}
                        />
                        {serviceData.isSingleService && (
                          <PaymentSummary
                            serviceData={serviceData}
                            numberOfParticipants={selectedNumberOfParticipants}
                            status={status}
                            isCart={isCart}
                            selectedProductAddons={selectedProductAddons}
                            relatedProducts={relatedProducts}
                          />
                        )}
                        <AddonsNotification serviceData={serviceData} />
                        <SmsNotification
                          isMSAutomationEnabled={isSMSAutomationsEnabled}
                        />
                        <PolicyDescription serviceData={serviceData} />
                        <ButtonsContainer
                          serviceData={serviceData}
                          isCart={isCart}
                          isServiceInCart={isServiceInCart}
                          errors={errors}
                          status={status}
                          selectedNumberOfParticipants={
                            selectedNumberOfParticipants
                          }
                          existingBookingDetails={existingBookingDetails}
                          memberDetails={memberDetails}
                          isSlotUnavailable={isSlotUnavailable}
                        />
                      </div>
                    </div>
                  </div>
                  {dialog ? (
                    <Dialog
                      {...dialog.props}
                      {...(dialog.type === DialogType.NoAvailablePaymentOptions
                        ? {
                            onConfirm: () => window.history.back(),
                            confirmButtonPrefixIcon: isRTL ? (
                              <ChevronRight aria-hidden />
                            ) : (
                              <ChevronLeft aria-hidden />
                            ),
                          }
                        : {})}
                    />
                  ) : null}
                  {hasNoValidIntakeSubmissionError && (
                    <Modal
                      dataHook="intake-form-pending-modal"
                      withoutCloseButton
                      modalTitle={t(
                        'app.intake-form-section.pending-modal.title',
                      )}
                      modalSubtitle={t(
                        'app.intake-form-section.pending-modal.subtitle',
                        {
                          formName:
                            pendingFormName ||
                            t(
                              'app.intake-form-section.pending-modal.form-name-fallback',
                            ),
                        },
                      )}
                      primaryButton={{
                        label: t('app.intake-form-section.pending-modal.cta'),
                        onClick: closePendingModal,
                      }}
                      secondaryButton={{
                        label: t(
                          'app.intake-form-section.pending-modal.contact',
                        ),
                        onClick: () => {
                          onContactBusiness();
                          closePendingModal();
                        },
                      }}
                      onClose={closePendingModal}
                    />
                  )}
                  <AlreadyBookedModal
                    existingBookingDetails={existingBookingDetails}
                    memberDetails={memberDetails}
                    serviceData={serviceData}
                  />
                  <CartErrorModal serviceData={serviceData} errors={errors} />
                  {cartModal?.status ? (
                    <CartModal
                      cartModalStatus={cartModal?.status}
                      serviceData={serviceData}
                      formSelectedSlot={formSelectedSlot}
                      bookingIds={cartModal.bookingIds}
                      settings={settings}
                    />
                  ) : null}
                </div>
              </div>
            </Suspense>
          </FormComponentContextProvider>
        </FormActionsProvider>
      </EditorContextProvider>
    </FormViewerContext>
  );
};

const RenderGlobalLoader = ({
  showGlobalLoader,
  dataHook,
}: {
  showGlobalLoader: boolean;
  dataHook?: string;
}) => {
  if (!showGlobalLoader) {
    return null;
  }
  return (
    <>
      <div
        className={st(classes.blanket)}
        data-hook={dataHook ? dataHook : WidgetDataHooks.GLOBAL_SPINNER}
      />
      <EmptyStatePage isProcessing={showGlobalLoader} />
    </>
  );
};

const renderEmptyStatePage = ({
  status,
  errors,
  error,
  serviceData,
  experiments,
}: {
  status: FormStatus;
  errors: FormError[];
  error?: Error | null;
  serviceData?: ServiceData;
  experiments?: ReturnType<typeof useExperiments>['experiments'];
}) => {
  const processingStatuses = [
    FormStatus.INITIALIZING,
    FormStatus.PROCESSING_USER_DETAILS,
    FormStatus.SSR,
  ];
  const isProcessing = processingStatuses.includes(status);

  const shouldShowEmptyStatePage = () =>
    isProcessing ||
    hasErrorOfType({ errorType: EmptyStateErrorType, errors }) ||
    error;

  if (shouldShowEmptyStatePage()) {
    if (error) {
      console.error('Empty state page rendered', error, errors);
    }
    return <EmptyStatePage isProcessing={isProcessing} />;
  }

  if (!serviceData?.slotServices) {
    console.error(
      'Empty state page rendered: some service not valid',
      error,
      errors,
    );
    return (
      <EmptyStatePage
        isProcessing={isProcessing}
        title="app.empty-state-page.no-pp.title"
        subtitle="app.empty-state-page.no-pp.subtitle"
      />
    );
  }

  return null;
};

const isPricingPlanSelected = (serviceData?: ServiceData | null) => {
  if (!serviceData?.slotServices) {
    return false;
  }

  return mapToArray<SlotService>(serviceData.slotServices).some(
    ({ selectedPaymentType }) =>
      selectedPaymentType === PaymentMethod.MEMBERSHIP,
  );
};

export default withSlotsPlaceholders(Widget);
